/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview Voice permission prompt and error handling component
 * @version 1.0.0
 * @since v0.0.07
 *
 * Purpose: Clear UX for mic permission failures, WS connection drops,
 * and unsupported browser paths. Inline alerts + retry guidance;
 * fallback to text-only flow if capture is blocked.
 *
 * Per FRONTEND_TODO.MD:
 * - Inline alerts + retry guidance
 * - Fallback to text-only flow if capture is blocked
 *
 * Dependencies:
 * - audioSettings for detectVoiceCapabilities()
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  detectVoiceCapabilities,
  type VoiceCapabilities
} from '../../config/audioSettings';
import { voicePermission } from '../../i18n/voiceMessages';

/**
 * Permission/error states
 */
export type PermissionState =
  | 'unknown'
  | 'checking'
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'unsupported'
  | 'error';

/**
 * Error types for specific messaging
 */
export type VoiceErrorType =
  | 'permission_denied'
  | 'no_microphone'
  | 'browser_unsupported'
  | 'connection_failed'
  | 'connection_lost'
  | 'generic';

export interface VoiceError {
  type: VoiceErrorType;
  message: string;
  canRetry: boolean;
  fallbackAvailable: boolean;
}

export interface VoicePermissionPromptProps {
  /** Current permission state (controlled) */
  permissionState?: PermissionState;
  /** Current error (if any) */
  error?: VoiceError | null;
  /** Callback when user requests to check/grant permission */
  onRequestPermission?: () => Promise<void>;
  /** Callback when user chooses text-only fallback */
  onFallbackToText?: () => void;
  /** Callback when user dismisses the prompt */
  onDismiss?: () => void;
  /** Callback when user retries after error */
  onRetry?: () => void;
  /** Show full modal or inline alert (default: inline) */
  variant?: 'inline' | 'modal';
  /** Optional CSS class name */
  className?: string;
}

/**
 * Error messages and guidance for each error type
 * Uses centralized messages from voiceMessages.ts
 */
const ERROR_CONTENT: Record<VoiceErrorType, {
  title: string;
  message: string;
  guidance: readonly string[];
  icon: string;
}> = {
  permission_denied: {
    title: voicePermission.errors.permission_denied.title,
    message: voicePermission.errors.permission_denied.message,
    guidance: voicePermission.errors.permission_denied.guidance,
    icon: '🚫'
  },
  no_microphone: {
    title: voicePermission.errors.no_microphone.title,
    message: voicePermission.errors.no_microphone.message,
    guidance: voicePermission.errors.no_microphone.guidance,
    icon: '🎤'
  },
  browser_unsupported: {
    title: voicePermission.errors.browser_unsupported.title,
    message: voicePermission.errors.browser_unsupported.message,
    guidance: voicePermission.errors.browser_unsupported.guidance,
    icon: '🌐'
  },
  connection_failed: {
    title: voicePermission.errors.connection_failed.title,
    message: voicePermission.errors.connection_failed.message,
    guidance: voicePermission.errors.connection_failed.guidance,
    icon: '📡'
  },
  connection_lost: {
    title: voicePermission.errors.connection_lost.title,
    message: voicePermission.errors.connection_lost.message,
    guidance: voicePermission.errors.connection_lost.guidance,
    icon: '⚡'
  },
  generic: {
    title: voicePermission.errors.generic.title,
    message: voicePermission.errors.generic.message,
    guidance: voicePermission.errors.generic.guidance,
    icon: '⚠️'
  }
};

/**
 * VoicePermissionPrompt Component
 *
 * Handles all voice-related permission and error states:
 * - Browser capability check
 * - Microphone permission request flow
 * - Error display with actionable guidance
 * - Fallback to text-only mode option
 *
 * Provides clear, user-friendly messaging and recovery paths.
 */
export const VoicePermissionPrompt: React.FC<VoicePermissionPromptProps> = ({
  permissionState: controlledPermissionState,
  error,
  onRequestPermission,
  onFallbackToText,
  onDismiss,
  onRetry,
  variant = 'inline',
  className = ''
}) => {
  const [internalPermissionState, setInternalPermissionState] = useState<PermissionState>('unknown');
  const [capabilities, setCapabilities] = useState<VoiceCapabilities | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);

  const permissionState = controlledPermissionState ?? internalPermissionState;

  /**
   * Check browser capabilities on mount
   */
  useEffect(() => {
    const caps = detectVoiceCapabilities();
    setCapabilities(caps);

    // Check if browser is unsupported
    if (!caps.webRTC || !caps.audioContext || !caps.webSocket) {
      setInternalPermissionState('unsupported');
      return;
    }

    // Check existing permission state (if Permissions API available)
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: 'microphone' as PermissionName })
        .then((result) => {
          if (result.state === 'granted') {
            setInternalPermissionState('granted');
          } else if (result.state === 'denied') {
            setInternalPermissionState('denied');
          } else {
            setInternalPermissionState('prompt');
          }

          // Listen for changes
          result.onchange = () => {
            if (result.state === 'granted') {
              setInternalPermissionState('granted');
            } else if (result.state === 'denied') {
              setInternalPermissionState('denied');
            }
          };
        })
        .catch(() => {
          // Permissions API not available - will check on request
          setInternalPermissionState('prompt');
        });
    } else {
      setInternalPermissionState('prompt');
    }
  }, []);

  /**
   * Handle permission request
   */
  const handleRequestPermission = useCallback(async () => {
    if (isRequesting) return;

    setIsRequesting(true);
    setInternalPermissionState('checking');

    try {
      if (onRequestPermission) {
        await onRequestPermission();
      } else {
        // Default: Try to get user media to trigger permission prompt
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the stream immediately - we just needed permission
        stream.getTracks().forEach((track) => track.stop());
      }
      setInternalPermissionState('granted');
    } catch (err) {
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setInternalPermissionState('denied');
        } else if (err.name === 'NotFoundError') {
          setInternalPermissionState('error');
        } else {
          setInternalPermissionState('error');
        }
      } else {
        setInternalPermissionState('error');
      }
    } finally {
      setIsRequesting(false);
    }
  }, [onRequestPermission, isRequesting]);

  /**
   * Get content for current error
   */
  const getErrorContent = () => {
    if (!error) return null;
    return ERROR_CONTENT[error.type] || ERROR_CONTENT.generic;
  };

  /**
   * Get content for permission state
   */
  const getPermissionContent = () => {
    switch (permissionState) {
      case 'unsupported':
        return {
          ...ERROR_CONTENT.browser_unsupported,
          showFallback: true,
          showRetry: false
        };
      case 'denied':
        return {
          ...ERROR_CONTENT.permission_denied,
          showFallback: true,
          showRetry: true
        };
      case 'prompt':
        return {
          title: voicePermission.prompt.title,
          message: voicePermission.prompt.message,
          guidance: voicePermission.prompt.guidance,
          icon: '🎤',
          showFallback: true,
          showRetry: false,
          showRequest: true
        };
      case 'checking':
        return {
          title: voicePermission.states.checking.title,
          message: voicePermission.states.checking.message,
          guidance: [],
          icon: '⏳',
          showFallback: false,
          showRetry: false
        };
      default:
        return null;
    }
  };

  // Error takes precedence
  const errorContent = getErrorContent();
  const permContent = getPermissionContent();
  const content = errorContent || permContent;

  // Nothing to show if granted and no error
  if (!content && permissionState === 'granted') {
    return null;
  }

  // Still checking/unknown - don't show anything yet
  if (!content) {
    return null;
  }

  const isModal = variant === 'modal';
  const errorId = error ? `voice-error-${error.type}` : undefined;
  const actionButtons: React.ReactNode[] = [];

  if ('showRequest' in content && content.showRequest) {
    actionButtons.push(
      <button
        key="request"
        onClick={handleRequestPermission}
        disabled={isRequesting}
        style={{ ...styles.button, ...styles.primaryButton }}
        aria-label={isRequesting ? voicePermission.buttons.enable.ariaLabelChecking : voicePermission.buttons.enable.ariaLabel}
        aria-busy={isRequesting}
      >
        {isRequesting ? `⏳ ${voicePermission.buttons.enable.checking}` : `🎤 ${voicePermission.buttons.enable.label}`}
      </button>
    );
  }

  if ((error?.canRetry || ('showRetry' in content && content.showRetry)) && onRetry) {
    actionButtons.push(
      <button
        key="retry"
        onClick={onRetry}
        style={{ ...styles.button, ...styles.primaryButton }}
        aria-label={voicePermission.buttons.retry.ariaLabel}
        aria-describedby={errorId ? `${errorId}-message` : undefined}
      >
        🔄 {voicePermission.buttons.retry.label}
      </button>
    );
  }

  if (
    (error?.fallbackAvailable || ('showFallback' in content && content.showFallback)) &&
    onFallbackToText
  ) {
    actionButtons.push(
      <button
        key="fallback"
        onClick={onFallbackToText}
        style={{ ...styles.button, ...styles.secondaryButton }}
        aria-label={voicePermission.buttons.fallback.ariaLabel}
        aria-describedby={errorId ? `${errorId}-message` : undefined}
      >
        💬 {voicePermission.buttons.fallback.label}
      </button>
    );
  }

  if (isModal && onDismiss) {
    actionButtons.push(
      <button
        key="dismiss"
        onClick={onDismiss}
        style={{ ...styles.button, ...styles.secondaryButton }}
        aria-label={voicePermission.buttons.dismiss.ariaLabel}
      >
        {voicePermission.buttons.dismiss.label}
      </button>
    );
  }

  return (
    <div
      className={`voice-permission-prompt ${variant} ${className}`}
      style={isModal ? styles.modalOverlay : styles.inlineContainer}
      role={isModal ? 'dialog' : 'alert'}
      aria-modal={isModal ? true : undefined}
      aria-labelledby={errorId ? `${errorId}-title` : undefined}
      aria-describedby={errorId ? `${errorId}-message` : undefined}
    >
      <div style={isModal ? styles.modalContent : styles.inlineContent}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.icon} aria-hidden="true">{content.icon}</span>
          <h3 style={styles.title} id={errorId ? `${errorId}-title` : undefined}>{content.title}</h3>
          {onDismiss && !isModal && (
            <button 
              onClick={onDismiss} 
              style={styles.dismissButton}
              aria-label={voicePermission.buttons.dismiss.ariaLabel}
            >
              ×
            </button>
          )}
        </div>

        {/* Message */}
        <p style={styles.message} id={errorId ? `${errorId}-message` : undefined} aria-live={error ? 'assertive' : 'polite'}>{content.message}</p>

        {/* Guidance Steps */}
        {content.guidance.length > 0 && (
          <ul style={styles.guidanceList} role="list">
            {content.guidance.map((step, index) => (
              <li key={index} style={styles.guidanceItem} role="listitem">
                <span style={styles.guidanceNumber} aria-hidden="true">{index + 1}</span>
                {step}
              </li>
            ))}
          </ul>
        )}

        {/* Browser Capabilities (for debugging) */}
        {/* TODO: [4.1.2 Help Text] Add a short explanation of what each capability means
            and a link to browser support documentation (e.g. "WebRTC is required for
            real-time audio streaming between your browser and the server"). */}
        {permissionState === 'unsupported' && capabilities && (
          <div style={styles.capabilitiesBox}>
            <div style={styles.capabilitiesTitle}>{voicePermission.capabilities.title}</div>
            <div style={styles.capabilitiesList} role="list" aria-label={voicePermission.capabilities.title}>
              <span style={capabilities.webRTC ? styles.capOk : styles.capFail} role="listitem" aria-label={`${voicePermission.capabilities.webRTC} ${capabilities.webRTC ? voicePermission.capabilities.supported : voicePermission.capabilities.notSupported}`}>
                {voicePermission.capabilities.webRTC} {capabilities.webRTC ? '✓' : '✗'}
              </span>
              <span style={capabilities.audioContext ? styles.capOk : styles.capFail} role="listitem" aria-label={`${voicePermission.capabilities.audio} ${capabilities.audioContext ? voicePermission.capabilities.supported : voicePermission.capabilities.notSupported}`}>
                {voicePermission.capabilities.audio} {capabilities.audioContext ? '✓' : '✗'}
              </span>
              <span style={capabilities.webSocket ? styles.capOk : styles.capFail} role="listitem" aria-label={`${voicePermission.capabilities.webSocket} ${capabilities.webSocket ? voicePermission.capabilities.supported : voicePermission.capabilities.notSupported}`}>
                {voicePermission.capabilities.webSocket} {capabilities.webSocket ? '✓' : '✗'}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        {actionButtons.length > 0 && <div style={styles.actions}>{actionButtons}</div>}
      </div>
    </div>
  );
};

/**
 * Component styles
 */
const styles: Record<string, React.CSSProperties> = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)'
  },
  modalContent: {
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    borderRadius: '16px',
    padding: '24px',
    maxWidth: '480px',
    width: '90%',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
  },
  inlineContainer: {
    width: '100%'
  },
  inlineContent: {
    backgroundColor: 'rgba(30, 30, 30, 0.9)',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px'
  },
  icon: {
    fontSize: '28px'
  },
  title: {
    flex: 1,
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
    fontFamily: '"Cinzel", serif'
  },
  dismissButton: {
    background: 'none',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1
  },
  message: {
    margin: '0 0 16px',
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: 1.5
  },
  guidanceList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 16px'
  },
  guidanceItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '8px 0',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.7)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
  },
  guidanceNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: 'rgba(139, 92, 246, 0.3)',
    color: '#a78bfa',
    fontSize: '11px',
    fontWeight: '600',
    flexShrink: 0
  },
  capabilitiesBox: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px'
  },
  capabilitiesTitle: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px'
  },
  capabilitiesList: {
    display: 'flex',
    gap: '12px',
    fontSize: '12px'
  },
  capOk: {
    color: '#22c55e'
  },
  capFail: {
    color: '#ef4444'
  },
  actions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  button: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '"Cinzel", serif'
  },
  primaryButton: {
    backgroundColor: '#8b5cf6',
    color: '#fff'
  },
  secondaryButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.8)',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  }
};

export default VoicePermissionPrompt;



